import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Button } from "../../../ui/Button";
import { displayToast } from "../../../app/AppToast";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../../../domains/user/hooks/useCurrentUser";
import { apiClient, revalidateApiQuery } from "../../../../services/api";
import {
  popSettingsToRoot,
  popSettingsView,
} from "../../../../domains/app/stores/settingsSheetStore";
import { SettingsSubHeader } from "../SettingsSubHeader";

export function LeaveRoomView({
  roomId,
  roomName,
}: {
  roomId: string;
  roomName: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  // useForm only for a submit-in-flight flag without owning `useState`.
  const { handleSubmit, formState } = useForm();

  async function onLeave(): Promise<void> {
    // Read before the call: `currentUser` still carries the room we are about
    // to drop.
    const wasLastRoom = currentUser !== null && currentUser.rooms.length <= 1;

    try {
      await apiClient.delete("/webapp/private/rooms/:id/leave", {
        param: { id: roomId },
      });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }

    displayToast(t("You've left the space"), "success");

    // Landing back on an empty rooms list is a dead end, and the root no longer
    // offers a way into it — go straight back to the settings root instead.
    if (wasLastRoom) {
      popSettingsToRoot();
    } else {
      popSettingsView();
    }

    await revalidateApiQuery("/webapp/private/rooms");
    void revalidateApiQuery("/webapp/private/playlists/recommended");
    await reloadCurrentUser();

    // With no room left, whatever sits under the sheet may be room-scoped
    // content the user just lost access to — land them on the global feed.
    if (wasLastRoom) {
      history.replace("/discovery");
    }
  }

  return (
    <div className="flex h-full flex-col">
      <SettingsSubHeader title={roomName} />
      <div className="flex flex-1 flex-col overflow-y-auto scroll-fog-y px-5">
        <h3 className="text-ui-highlight font-semibold text-ds-black">
          {t("Leave this Room?")}
        </h3>
        <p className="mt-2 text-ui-note text-ds-slate-600">
          {t(
            "You'll lose access to exclusive content and updates from this community. You can always rejoin later."
          )}
        </p>
      </div>
      <form
        className="shrink-0 flex flex-col gap-3 px-5 pt-2 pb-6"
        onSubmit={handleSubmit(onLeave)}
      >
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={popSettingsView}
        >
          {t("Cancel")}
        </Button>
        <Button
          type="submit"
          variant="destructive"
          size="lg"
          className="w-full"
          disabled={formState.isSubmitting}
        >
          {t("Leave Room")}
        </Button>
      </form>
    </div>
  );
}
